import { TreeReference, type Campaign } from '@datapad/data-model';
import {
	Box,
	FormControl,
	MenuItem,
	Select,
	Typography,
	type SelectChangeEvent,
} from '@mui/material';
import type React from 'react';
import { menuLabelSx, menuLabelTypographyVariant, menuSectionSx } from './Constants.js';

/**
 * {@link CampaignSelector} input props.
 */
export interface CampaignSelectorProps {
	/**
	 * The available campaigns for the current character.
	 */
	campaigns: readonly Campaign[];

	/**
	 * Reference to the currently selected campaign.
	 */
	currentCampaign: TreeReference | undefined;

	/**
	 * Callback when the campaign selection changes.
	 */
	changeSelectedCampaign(newCampaign: TreeReference | undefined): void;
}

/**
 * Datapad menu campaign-selection drop-down selector.
 *
 * @remarks
 * Only displayed when the current character belongs to multiple campaigns.
 * Allows switching between campaigns to view different campaign dates and contexts.
 */
export function CampaignSelector(props: CampaignSelectorProps): React.ReactElement {
	const { campaigns, currentCampaign: currentCampaignRef, changeSelectedCampaign } = props;

	const currentCampaign = campaigns.find((campaign) =>
		currentCampaignRef?.isReferenceTo(campaign),
	);

	const handleChange = (event: SelectChangeEvent<string>): void => {
		const selectedId = event.target.value;
		const selected = campaigns.find((c) => c.id === selectedId);
		if (selected) {
			changeSelectedCampaign(TreeReference.createReferenceToNode(selected));
		}
	};

	return (
		<Box sx={menuSectionSx}>
			<Typography variant={menuLabelTypographyVariant} sx={menuLabelSx}>
				Campaign
			</Typography>

			<FormControl fullWidth size="small">
				<Select
					value={currentCampaign?.id ?? ''}
					onChange={handleChange}
					displayEmpty
					sx={{ bgcolor: 'background.paper' }}
				>
					{campaigns.map((campaign) => (
						<MenuItem key={campaign.id} value={campaign.id}>
							{campaign.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	);
}
